<template>
  <div class="page-product">
    <div class="row mb-16">
      <div class="col-md-7 px-0 md:px-4">
        <product-preview
          ref="productPreview"
          :selected-color="selectedColor"
          :product="product"
          :enable-personalization="!!product.personalizationTemplate"
          :disable-personalization-mockups="disablePersonalizationMockups"
          :required-personalization-layers="requiredPersonalizationLayers"
          :personalization-layer="personalizationLayer"
          @canvas-initialised="initCanvasForLayer"
          @color-selected="selectedColor = $event"
        />
      </div>
      <div class="col-md-5 px-0 md:px-4">
        <product-info
          :product="product"
          class="px-4"
          :class="{
            'pt-5': mobile,
          }"
          :override-add-to-cart="!!product.personalizationTemplate"
          :show-from-min-price="!!product.personalizationTemplate"
          :disable-size-selector="hasBulkPersonalization"
          :disable-quantity-selector="hasBulkPersonalization"
          :selected-color="selectedColor"
          :personalization-sizes="personalizationSelectedSizes"
          :addon-price="personalizationAddonPrice"
          :addon-active-rows="personalizationActiveRows"
          :breakdown-items="personalizationBreakdownItems"
          @color-selected="selectedColor = $event"
          @custom-add-to-cart="onPersonalizationAddToCart"
        >
          <template
            v-if="product.personalizationTemplate && selectedColor"
            #personalization="{color}"
          >
            <personalization-form
              :canvases="layerCanvases"
              :selected-color="selectedColor"
              :product="product"
              :disable-personalization-mockups="disablePersonalizationMockups"
              :background-color="color ? color.background : 'transparent'"
              @form-data-updated="personalizationFormData = $event"
              @bulk-form-data-updated="bulkPersonalizationFormData = $event"
              @order-total-changed="onOrderTotalChanged"
              @focus-item="onFocusPersonalizationItem"
              @blur-item="onBlurPersonalizationItem"
            />
          </template>
          <template v-if="hasReviews && hasReviewsEnabled" #rating>
            <product-rating
              class="mt-2"
              :rating="productRating"
              :rating-count="productRatingCount"
              @clicked="scrollToReviews()"
            />
          </template>
        </product-info>
      </div>
    </div>

    <div class="row mb-16">
      <div class="col-12">
        <product-spec :product="product" />
      </div>
    </div>

    <div v-if="hasReviewsEnabled" ref="reviewsContainer" class="row mb-16">
      <div class="col-12">
        <tml-loader :loading="!product" height="500px" :loading-ratio="0.5">
          <div v-if="hasReviews" class="mb-8">
            <h4 class="mb-4">{{ product?.name }} Reviews</h4>
            <p>
              Recent customer reviews gave our {{ product?.name }}
              {{ productRating }}/5 on average.
            </p>
            <div v-if="hasReviewsSummaryEnabled" class="mt-4">
              <h5 class="mb-2">
                Our Customers Say
                <font-awesome-icon :icon="faMicrochipAi" />
              </h5>
              <p>
                {{ product.reviews.summary }}
              </p>
            </div>
          </div>
          <h4 v-else>Our {{ product?.name }} hasn't had any reviews yet</h4>
          <product-reviews
            :product-id="product.id"
            :show-replies="hasReviewRepliesEnabled"
          />
          <tml-button
            class="mt-4"
            :style="{
              width: mobile ? '100%' : '300px',
            }"
            text="Submit review"
            @click="$overlays.open('share-review-popup')"
          />
        </tml-loader>
      </div>
    </div>

    <template v-if="product">
      <size-chart-popup
        v-if="!product.isBundle && product.sizeChartId"
        :product="product"
      />
      <template v-else>
        <template v-for="item in uniqueBundleItems" :key="item.id">
          <size-chart-popup v-if="item.sizeChartId" :product="item" />
        </template>
      </template>
      <product-review-popup :product="product" />
    </template>
  </div>
</template>

<script>
import {defineAsyncComponent} from 'vue';
import uniqBy from 'lodash/uniqBy';
import {StoreMixin} from '@teemill/components/mixins';
import {viewportSize} from '@teemill/common/helpers';
import {formatUrl} from '@teemill/common';

import AddToCartPopup from './AddToCartPopup.vue';
import ProductReviewPopup from './ProductReviewPopup.vue';
import SizeChartPopup from './SizeChartPopup.vue';
import ProductInfo from './ProductInfo.vue';
import ProductSpec from './ProductSpec.vue';
import ProductReviews from './ProductReviews.vue';
import ProductPreview from './ProductPreview.vue';
import ProductRating from './ProductRating.vue';
import {http} from '@teemill/common/services';
import {SierraCanvas, Vector} from '@teemill/common/classes';
import {
  isPersonalizationValueExportable,
  normalisePersonalizationValue,
  sanitizeMetadataValue,
} from '../helpers/personalizationExport';

import {Profanity, ProfanityOptions} from '@2toad/profanity';
import {faMicrochipAi} from '@fortawesome/pro-light-svg-icons';

const profanityOptions = new ProfanityOptions();
profanityOptions.wholeWord = false;
const profanity = new Profanity(profanityOptions);

export default {
  name: 'ProductShow',

  components: {
    AddToCartPopup,
    ProductPreview,
    ProductReviewPopup,
    SizeChartPopup,
    ProductInfo,
    ProductSpec,
    ProductReviews,
    ProductRating: defineAsyncComponent(() => import('./ProductRating.vue')),
    PersonalizationForm: defineAsyncComponent(() =>
      import('./PersonalizationForm.vue')
    ),
  },
  mixins: [StoreMixin],

  inject: ['theme'],

  provide() {
    return {
      setVuexProperty: this.setVuexProperty,
      getVuexProperty: this.getVuexProperty,
    };
  },

  props: {
    product: Object,
  },

  data() {
    return {
      faMicrochipAi,

      selectedColor: null,
      layerCanvases: {},
      personalizationFormData: [],
      bulkPersonalizationFormData: [],
      personalizationAddonPrice: 0,
      personalizationActiveRows: 0,
      personalizationBreakdownItems: [],
      personalizationLayer: null,

      hasUnsavedChanges: false,
    };
  },

  computed: {
    uniqueBundleItems() {
      return uniqBy(this.product.bundleItems, 'id');
    },

    mobile() {
      return viewportSize.isSmaller('md');
    },

    productRating() {
      return this.product.reviews.rating;
    },

    productRatingCount() {
      return this.product.reviews.count;
    },

    hasReviews() {
      return !!this.product.reviews.raw.length;
    },

    disablePersonalizationMockups() {
      if (!this.product.personalizationTemplate) {
        return false;
      }

      try {
        const template = JSON.parse(this.product.personalizationTemplate);
        return template?.config?.personalization?.disableMockups === true;
      } catch {
        return false;
      }
    },

    requiredPersonalizationLayers() {
      if (!this.product.personalizationTemplate) {
        return ['front'];
      }

      const templateData = JSON.parse(this.product.personalizationTemplate);
      const objects = Array.isArray(templateData.objects)
        ? templateData.objects
        : Object.values(templateData.objects ?? {});

      const layerOrder = ['front', 'back', 'left', 'right', 'neck'];
      const dynamicLayers = Array.from(
        new Set(
          objects
            .map(object => object?.layer)
            .filter(layer => typeof layer === 'string' && layer.length > 0)
        )
      );

      if (!dynamicLayers.length) {
        return ['front'];
      }

      return dynamicLayers.sort((a, b) => {
        const aIndex = layerOrder.indexOf(a);
        const bIndex = layerOrder.indexOf(b);
        const safeAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const safeBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

        if (safeAIndex !== safeBIndex) {
          return safeAIndex - safeBIndex;
        }

        return a.localeCompare(b);
      });
    },

    hasBulkPersonalization() {
      if (!this.bulkPersonalizationFormData) {
        return false;
      }

      if (Array.isArray(this.bulkPersonalizationFormData)) {
        return !!this.bulkPersonalizationFormData.length;
      }

      return !!Object.keys(this.bulkPersonalizationFormData).length;
    },

    hasReviewsEnabled() {
      if (this.$store.state.subdomain.reviews === null) {
        return true;
      }

      return !!this.$store.state.subdomain.reviews?.enabled;
    },

    hasReviewRepliesEnabled() {
      if (this.$store.state.subdomain.reviews?.showReplies === undefined) {
        return true;
      }

      return !!this.$store.state.subdomain.reviews?.showReplies;
    },

    hasReviewsSummaryEnabled() {
      if (!this.product.reviews.summary) {
        return false;
      }

      return !!this.$store.state.subdomain.reviews?.showSummary;
    },

    personalizationSelectedSizes() {
      if (!Array.isArray(this.bulkPersonalizationFormData)) {
        return [];
      }

      return this.bulkPersonalizationFormData
        .map(record => record?.size?.value)
        .filter(Boolean);
    },
  },

  watch: {
    hasUnsavedChanges(newValue) {
      this.$emit('unsaved-changes', newValue);
    },
    personalizationFormData: {
      handler(newValue, oldValue) {
        if (oldValue.length) {
          this.hasUnsavedChanges = true;
        }
      },
      deep: true,
    },
    bulkPersonalizationFormData: {
      handler(newValue, oldValue) {
        if (oldValue.length) {
          this.hasUnsavedChanges = true;
        }
      },
      deep: true,
    },
  },

  created() {
    this.registerVuexModule();
  },

  methods: {
    onOrderTotalChanged({total, activeRows, breakdownItems}) {
      this.personalizationAddonPrice = total;
      this.personalizationActiveRows = activeRows;
      this.personalizationBreakdownItems = breakdownItems;
    },

    getCanvasForLayer(layer) {
      return this.layerCanvases?.[layer] ?? null;
    },

    scrollToReviews() {
      if (this.$refs.reviewsContainer) {
        const headerElement = document.querySelector('.header');
        const headerHeight = headerElement ? headerElement.offsetHeight : 80;

        window.scrollTo({
          top: this.$refs.reviewsContainer.offsetTop - (headerHeight + 16),
          behavior: 'smooth',
        });
      }
    },

    initCanvasForLayer({layer, canvas}) {
      this.layerCanvases = {
        ...this.layerCanvases,
        [layer]: canvas,
      };
    },

    _sanitizeMetadataValue(value) {
      return sanitizeMetadataValue(value);
    },

    _normalisePersonalizationValue(value) {
      return normalisePersonalizationValue(value);
    },

    _isPersonalizationValueExportable(formItem, value = formItem?.value) {
      return isPersonalizationValueExportable(formItem, value);
    },

    _buildPersonalizationMetadata({
      mode,
      color,
      size,
      fields,
      exportedFieldIds = [],
      rowIndex = null,
    }) {
      const exportedSet = new Set(
        (exportedFieldIds ?? []).map(id => `${id ?? ''}`)
      );

      const payloadFields = (fields ?? []).map(field => {
        const normalisedValue = this._normalisePersonalizationValue(
          field?.value
        );
        const sanitizedValue = this._sanitizeMetadataValue(normalisedValue);
        const key = `${field?.id ?? ''}`;

        return {
          id: field?.id ?? null,
          label: field?.label ?? null,
          type: field?.type ?? null,
          layer: field?.layer ?? null,
          exportable: this._isPersonalizationValueExportable(
            field,
            normalisedValue
          ),
          exported: exportedSet.has(key),
          ...(sanitizedValue !== null && sanitizedValue !== undefined
            ? {value: sanitizedValue}
            : {}),
        };
      });

      return {
        mode,
        colour: color ?? null,
        size: size ?? null,
        ...(rowIndex !== null ? {rowIndex} : {}),
        fields: payloadFields,
      };
    },

    async onPersonalizationAddToCart({color, size, response, reject}) {
      if (this.bulkPersonalizationFormData?.length) {
        this.onBulkPersonalizationAddToCart({
          color,
          size,
          response,
          reject,
        });

        return;
      }

      let applications;
      let personalizationMetadata;
      let frontDesignFile;
      let backDesignFile;

      try {
        const restore = this._hidePlaceholderTemplateObjects();
        try {
          ({frontDesignFile, backDesignFile} = await this.exportCanvasLayers());
        } finally {
          restore();
        }

        const personalizationResult =
          await this.buildPersonalizationApplications();
        applications = personalizationResult?.applications ?? null;
        personalizationMetadata = this._buildPersonalizationMetadata({
          mode: 'single',
          color,
          size,
          fields: this.personalizationFormData,
          exportedFieldIds: personalizationResult?.exportedFieldIds ?? [],
        });
      } catch {
        snackbar.error(
          'Something went wrong uploading your designs. Please try again.'
        );
        reject();
        return;
      }

      let rejected = false;

      this.personalizationFormData.forEach(data => {
        if (data.rules?.profanityFilter) {
          const object = this.getCanvasForLayer(data.layer)?.findObject(
            data.id
          );

          if (object && profanity.exists(object.text)) {
            snackbar.error('Please remove profane language');
            reject();
            rejected = true;
          }
        }
      });

      if (rejected) {
        return;
      }

      const {data: newProduct} = await http.post(
        formatUrl(`/omnis/v3/studio/createFrontendProduct/${this.product.id}`),
        {
          sku: this.product.baseSku,
          colours: [color],
          sizes: [size],
          front_design_base64: frontDesignFile,
          back_design_base64: backDesignFile,
          applications,
          personalizationMetadata,
          name: `${this.product.name} (Custom)`,
        }
      );

      this.$store
        .dispatch('cart/addItem', {
          item: {
            productId: newProduct.id,
            productName: newProduct.name,
            productColor: color,
            productSize: size,
            productPrice: this.product.price,
          },
          name: 'main',
        })
        .then(addToCartResponse => {
          this.hasUnsavedChanges = false;
          response(addToCartResponse);
        })
        .catch(() => {
          reject();
        });
    },

    async onBulkPersonalizationAddToCart({color, size, response, reject}) {
      const sizes = Object.values(this.bulkPersonalizationFormData).map(
        data => data.size?.value
      );

      if (sizes.includes(undefined)) {
        snackbar.error('Please select a size for each item');
        reject();
        return;
      }

      let layerData;

      try {
        layerData = await this.bulkExportLayers(color);
      } catch {
        snackbar.error(
          'Something went wrong uploading your designs. Please try again.'
        );
        reject();
        return;
      }

      const createdProducts = [];

      const productCreationPromises = [];

      for (let i = 0, l = layerData.length; i < l; ++i) {
        productCreationPromises.push(
          new Promise((resolve, reject) => {
            http
              .post(
                formatUrl(
                  `/omnis/v3/studio/createFrontendProduct/${this.product.id}`
                ),
                {
                  sku: this.product.baseSku,
                  colours: [color],
                  sizes: [
                    this.bulkPersonalizationFormData[i]?.size?.value ?? size,
                  ],
                  front_design_base64: layerData[i].frontDesignFile,
                  back_design_base64: layerData[i].backDesignFile,
                  applications: layerData[i].applications,
                  personalizationMetadata: layerData[i].personalizationMetadata,
                  name: `${this.product.name} (Custom)`,
                }
              )
              .then(({data}) => {
                resolve(data);
                createdProducts[i] = data;
              });
          })
        );
      }

      Promise.all(productCreationPromises).then(() => {
        this.$store
          .dispatch('cart/bulkAddItems', {
            items: createdProducts.map((newProduct, index) => ({
              productId: newProduct.id,
              productName: newProduct.name,
              productColor: color,
              productSize:
                this.bulkPersonalizationFormData[index]?.size?.value ?? size,
              quantity: 1,
            })),
            name: 'main',
          })
          .then(addToCartResponse => {
            this.hasUnsavedChanges = false;
            response(addToCartResponse);
          })
          .catch(() => {
            reject();
          });
      });
    },

    async bulkExportLayers(color = null) {
      const keys = Object.keys(this.bulkPersonalizationFormData);

      // an "item" being an individual item in the bulk data (i.e. a row in the template form)
      const items = [];

      for (let i = 0; i < keys.length; i++) {
        items.push(await this._bulkExportLayer(i, color));
      }

      const allBlobs = items.flatMap(i => [
        ...(i?.staticExports ?? []).map(e => e.blob),
        ...(i?.exported ?? []).map(e => e.exportResult.blob),
      ]);

      if (!allBlobs.length) {
        return items.map(i => ({
          frontDesignFile: i?.frontDesignFile,
          backDesignFile: i?.backDesignFile,
          applications: null,
          personalizationMetadata: i?.personalizationMetadata ?? null,
        }));
      }

      const allFileIds = await this._uploadPersonalizationImages(allBlobs);

      if (allFileIds.length !== allBlobs.length || allFileIds.some(id => !id)) {
        return items.map(i => ({
          frontDesignFile: i?.frontDesignFile,
          backDesignFile: i?.backDesignFile,
          applications: null,
          personalizationMetadata: i?.personalizationMetadata ?? null,
        }));
      }

      const layers = this.product?.layers ?? {};
      let offset = 0;
      return items.map(i => {
        const staticCount = i?.staticExports?.length ?? 0;
        const exportedCount = i?.exported?.length ?? 0;

        const staticFileIds = allFileIds.slice(offset, offset + staticCount);
        offset += staticCount;
        const elementFileIds = allFileIds.slice(offset, offset + exportedCount);
        offset += exportedCount;

        if (i?.staticExports === null || !exportedCount) {
          return {
            frontDesignFile: i?.frontDesignFile,
            backDesignFile: i?.backDesignFile,
            applications: null,
            personalizationMetadata: i?.personalizationMetadata ?? null,
          };
        }

        const applications = [];

        for (let j = 0; j < staticCount; j++) {
          const {layer} = i.staticExports[j];
          const layerTechs = layers?.[layer]?.technologies ?? [];
          applications.push({
            placement: layer,
            technology: layerTechs[0] ?? 'dtf',
            files: [{id: staticFileIds[j]}],
          });
        }

        const perElement = this._buildApplicationsFromExports(
          i.exported,
          elementFileIds
        );
        if (!perElement) {
          return {
            frontDesignFile: i?.frontDesignFile,
            backDesignFile: i?.backDesignFile,
            applications: null,
            personalizationMetadata: i?.personalizationMetadata ?? null,
          };
        }
        applications.push(...perElement);

        return {
          frontDesignFile: i?.frontDesignFile,
          backDesignFile: i?.backDesignFile,
          applications,
          personalizationMetadata: i?.personalizationMetadata ?? null,
        };
      });
    },

    async _bulkExportLayer(index, color = null) {
      const record = this.bulkPersonalizationFormData[index];

      if (!record) {
        return null;
      }

      let rejected = false;

      record.items.forEach(item => {
        if (!this._isPersonalizationValueExportable(item, item.value)) {
          return;
        }

        const object = this.getCanvasForLayer(item.layer)?.findObject(item.id);

        if (!object) {
          return;
        }

        if (object.className === 'Text') {
          let textValue = item.value;
          if (typeof textValue !== 'string') {
            textValue = textValue.toString();
          }

          if (item.type === 'number') {
            textValue = textValue.replace(/[^\d-]/g, '');

            if (item.rules.pad) {
              textValue = textValue.padStart(item.rules.pad, '0');
            }
          } else if (item.rules?.profanityFilter) {
            textValue = profanity.censor(textValue);
          }

          object.setText(textValue);
          object._updatePerspectiveCanvas();
        }

        if (item.rules?.profanityFilter) {
          if (profanity.exists(object.text)) {
            snackbar.error('Please remove profane language');
            rejected = true;
          }
        }
      });

      if (rejected) {
        throw new Error('Profanity detected');
      }

      const activeIds = new Set([
        ...record.items
          .filter(item =>
            this._isPersonalizationValueExportable(item, item.value)
          )
          .map(item => item.id), // bulk text fields with input
        ...this.personalizationFormData
          .filter(
            item =>
              item.render &&
              this._isPersonalizationValueExportable(item, item.value)
          )
          .map(item => item.id), // global images that have been uploaded
      ]);

      const restore = this._hidePlaceholderTemplateObjects(activeIds);
      let frontDesignFile, backDesignFile;
      try {
        ({frontDesignFile, backDesignFile} = await this.exportCanvasLayers());
      } finally {
        restore();
      }

      const exported = await this._exportPersonalizationElements(activeIds);
      const staticExports = exported?.length
        ? await this._exportStaticDesignBlobs()
        : [];

      const personalizationMetadata = this._buildPersonalizationMetadata({
        mode: 'bulk',
        color,
        size: record.size?.value ?? null,
        rowIndex: index,
        fields: [
          ...record.items,
          ...this.personalizationFormData.filter(item => item.render),
        ],
        exportedFieldIds: (exported ?? []).map(
          item => item?.formItem?.id ?? null
        ),
      });

      return {
        frontDesignFile,
        backDesignFile,
        exported,
        staticExports,
        personalizationMetadata,
      };
    },

    onFocusPersonalizationItem({layer}) {
      if (this.disablePersonalizationMockups) {
        return;
      }

      this.personalizationLayer = layer;
      this.$refs.productPreview?.showPersonalizationLayer(layer);
    },

    onBlurPersonalizationItem() {
      this.personalizationLayer = null;
    },

    // Exports per-element template images and a static-only design for each
    // layer, then returns a combined applications array. Returns null when
    // any export/upload fails so the caller falls back to the full design file.
    async buildPersonalizationApplications() {
      const exported = await this._exportPersonalizationElements();
      if (!exported?.length) {
        return {
          applications: null,
          exportedFieldIds: [],
        };
      }

      const staticExports = await this._exportStaticDesignBlobs();
      if (staticExports === null) {
        return {
          applications: null,
          exportedFieldIds: [],
        };
      }

      const allBlobs = [
        ...staticExports.map(e => e.blob),
        ...exported.map(e => e.exportResult.blob),
      ];
      const fileIds = await this._uploadPersonalizationImages(allBlobs);

      if (fileIds.length !== allBlobs.length || fileIds.some(id => !id)) {
        return {
          applications: null,
          exportedFieldIds: [],
        };
      }

      const layers = this.product?.layers ?? {};
      const applications = [];

      for (let i = 0; i < staticExports.length; i++) {
        const {layer} = staticExports[i];
        const layerTechs = layers?.[layer]?.technologies ?? [];
        applications.push({
          placement: layer,
          technology: layerTechs[0] ?? 'dtf',
          files: [{id: fileIds[i]}],
        });
      }

      const elementFileIds = fileIds.slice(staticExports.length);
      const perElement = this._buildApplicationsFromExports(
        exported,
        elementFileIds
      );
      if (!perElement) {
        return {
          applications: null,
          exportedFieldIds: [],
        };
      }
      applications.push(...perElement);

      return {
        applications,
        exportedFieldIds: exported.map(item => item?.formItem?.id ?? null),
      };
    },

    // Renders each personalization element and collects blobs + metadata.
    // Returns null if any exportable element fails
    // activeIds are the ids of elements we want to export (all elements if not provided)
    async _exportPersonalizationElements(activeIds) {
      const exported = [];

      for (const formItem of this.personalizationFormData) {
        if (activeIds) {
          if (!activeIds.has(formItem.id)) {
            continue;
          }
        } else if (!this._isPersonalizationValueExportable(formItem)) {
          continue;
        }

        const canvas = this.getCanvasForLayer(formItem.layer);
        if (!canvas) {
          continue;
        }

        const canvasObject = canvas.findObject(formItem.id);
        if (!canvasObject) {
          continue;
        }

        let exportResult = await this._exportPersonalizationElement(
          canvas,
          canvasObject
        );

        if (!exportResult) {
          canvas?.render?.();
          await new Promise(r => setTimeout(r, 50));
          exportResult = await this._exportPersonalizationElement(
            canvas,
            canvasObject
          );
        }

        if (!exportResult) {
          return null;
        }

        exported.push({formItem, canvas, canvasObject, exportResult});
      }

      return exported;
    },

    // Exports each layer with template objects hidden, producing a blob that
    // contains only the static (non-personalised) artwork. Returns an array
    // of {blob, layer} entries for layers that have static content.
    async _exportStaticDesignBlobs() {
      const templateObjects = new Set();
      for (const formItem of this.personalizationFormData) {
        const obj = this.getCanvasForLayer(formItem.layer)?.findObject(
          formItem.id
        );
        if (obj) templateObjects.add(obj);
      }

      const results = [];
      let expectedStaticLayers = 0;

      for (const layer of ['front', 'back']) {
        const canvas = this.getCanvasForLayer(layer);
        if (!canvas) continue;

        const objects = canvas.getObjects(layer);
        const hasStaticContent = objects.some(
          obj =>
            obj.enabled &&
            !obj?.applicationGroupRef &&
            !templateObjects.has(obj)
        );
        if (!hasStaticContent) continue;

        expectedStaticLayers++;

        const visibilitySnapshot = objects.map(obj => ({
          obj,
          enabled: obj.enabled,
        }));
        objects.forEach(obj => {
          if (obj?.applicationGroupRef || templateObjects.has(obj)) {
            obj.enabled = false;
          }
        });

        let base64;
        try {
          const designScale = 2584 / canvas.boundingBox.width;
          base64 = canvas.export('image/png', 0.92, designScale, 1, false);
        } finally {
          visibilitySnapshot.forEach(({obj, enabled}) => {
            obj.enabled = enabled;
          });
        }

        if (!base64) continue;

        const dataUrl = base64.startsWith('data:')
          ? base64
          : `data:image/png;base64,${base64}`;
        const res = await fetch(dataUrl);
        const blob = await res.blob();

        if (blob?.size) {
          results.push({blob, layer});
        }
      }

      if (results.length < expectedStaticLayers) {
        return null;
      }

      return results;
    },

    // Pairs exported element metadata with uploaded file IDs to build the applications payload.
    // Returns null if any uploads are missing so StudioController falls back to the full design file.
    _buildApplicationsFromExports(exported, fileIds) {
      if (fileIds.length !== exported.length || fileIds.some(id => !id)) {
        return null;
      }

      const layers = this.product?.layers ?? {};
      const applications = [];

      for (let i = 0; i < exported.length; i++) {
        const fileId = fileIds[i];

        const {formItem, canvas, canvasObject, exportResult} = exported[i];
        const layerObject = canvas?.getLayerByName?.(formItem.layer);
        const boundsWidth =
          layerObject?.bounds?.width ??
          canvas?.boundingBox?.width ??
          canvas?.params?.stage?.width;
        const {croppedBounds, placementOrigin} = exportResult;

        const layerTechs = layers?.[formItem.layer]?.technologies ?? [];
        const technology = layerTechs[0] ?? 'dtf';

        applications.push({
          placement: formItem.layer,
          technology,
          template_field: formItem.label,
          template_object_id: formItem.id,
          properties: {
            width: croppedBounds.right - croppedBounds.left,
            height: croppedBounds.bottom - croppedBounds.top,
            positionX: Math.max(0, croppedBounds.left - placementOrigin.x),
            positionY: Math.max(0, croppedBounds.top - placementOrigin.y),
            positionZ: canvasObject.z ?? 0,
            originalCanvasWidth: boundsWidth ?? 0,
          },
          files: [{id: fileId}],
        });
      }

      return applications;
    },

    // Renders a single canvas object in isolation and returns a PNG blob cropped to its bounding box.
    async _exportPersonalizationElement(canvas, targetObject) {
      const renderer = canvas?.renderer;
      if (!renderer) {
        return null;
      }

      const layerName = targetObject.layer ?? canvas?.activeLayerName;
      const layerObject = layerName
        ? canvas?.getLayerByName?.(layerName)
        : null;
      const baseBounds = layerObject?.bounds ?? canvas.boundingBox;
      const placementLeft = baseBounds.x ?? baseBounds.left ?? 0;
      const placementTop = baseBounds.y ?? baseBounds.top ?? 0;
      const placementRight = placementLeft + (baseBounds.width ?? 0);
      const placementBottom = placementTop + (baseBounds.height ?? 0);

      const targetDesignWidth = (() => {
        if (baseBounds?.pixelsPerCm && baseBounds?.cmToInch) {
          const targetInchSize =
            baseBounds.width / baseBounds.pixelsPerCm / baseBounds.cmToInch;
          const targetDpi = 150;
          return Math.max(targetInchSize * targetDpi, 2584);
        }
        return 2584;
      })();
      const exportScale =
        targetDesignWidth / (baseBounds.width ?? targetDesignWidth);

      // Hide every sibling on the same layer so only targetObject renders.
      const layerObjects = canvas?.getObjects?.(layerName) ?? [];
      const visibilitySnapshot = layerObjects.map(obj => ({
        obj,
        enabled: obj.enabled,
      }));

      visibilitySnapshot.forEach(({obj}) => {
        obj.enabled = obj === targetObject;
      });

      // Snapshot canvas state before mutating for export.
      const originalActiveLayer = canvas?.activeLayerName;
      const canvasScaleSnapshot = canvas?.params?.scale;
      const globalTranslationSnapshot = canvas?.params?.globalTranslation
        ? new Vector(
            canvas.params.globalTranslation.x,
            canvas.params.globalTranslation.y
          )
        : null;

      const stashedParams = renderer.stashExportParams();
      renderer.engineParams.exportMode = true;

      let outputCanvas;
      try {
        // Render the canvas at print resolution with only the target visible.
        if (layerName) {
          canvas?.setActiveLayer?.(layerName);
        }
        renderer.prepareExport(exportScale, 1, true);

        renderer.render({
          includeBounds: false,
          includeStaticLayerImages: false,
          withAntiAliasing: true,
          async: false,
        });

        // we build a scan area around the object in the source canvas and trim transparency from the edges
        const sourceCanvas = renderer.virtualCanvas.canvas;
        const canvasScale = renderer.engineParams.scale ?? exportScale;

        // placement area in pixel coordinates
        const scanLeft = Math.max(0, Math.floor(placementLeft * canvasScale));
        const scanTop = Math.max(0, Math.floor(placementTop * canvasScale));
        const scanRight = Math.min(
          sourceCanvas.width,
          Math.ceil(placementRight * canvasScale)
        );
        const scanBottom = Math.min(
          sourceCanvas.height,
          Math.ceil(placementBottom * canvasScale)
        );

        const scanWidth = scanRight - scanLeft;
        const scanHeight = scanBottom - scanTop;

        if (scanWidth <= 0 || scanHeight <= 0) {
          return null;
        }

        const sourceCtx = sourceCanvas.getContext('2d');
        const imageData = sourceCtx.getImageData(
          scanLeft,
          scanTop,
          scanWidth,
          scanHeight
        );
        const pixels = imageData.data;

        let opaqueMinX = scanWidth;
        let opaqueMinY = scanHeight;
        let opaqueMaxX = -1;
        let opaqueMaxY = -1;

        // pixels is an array of pixels of the form [red, green, blue, alpha]. where alpha is the opacity of the pixel.
        for (let y = 0; y < scanHeight; y++) {
          const rowOffset = y * scanWidth;
          for (let x = 0; x < scanWidth; x++) {
            if (pixels[(rowOffset + x) * 4 + 3] > 0) {
              if (x < opaqueMinX) opaqueMinX = x;
              if (x > opaqueMaxX) opaqueMaxX = x;
              if (y < opaqueMinY) opaqueMinY = y;
              if (y > opaqueMaxY) opaqueMaxY = y;
            }
          }
        }

        // If the object is completely transparent, return null.
        if (opaqueMaxX < 0) {
          return null;
        }

        const integerLeft = scanLeft + opaqueMinX;
        const integerTop = scanTop + opaqueMinY;
        const integerRight = scanLeft + opaqueMaxX + 1;
        const integerBottom = scanTop + opaqueMaxY + 1;

        const visibleWidth = integerRight - integerLeft;
        const visibleHeight = integerBottom - integerTop;

        outputCanvas = new SierraCanvas(
          Math.max(1, visibleWidth),
          Math.max(1, visibleHeight)
        );

        if (visibleWidth > 0 && visibleHeight > 0) {
          outputCanvas.drawImage(
            sourceCanvas,
            integerLeft,
            integerTop,
            visibleWidth,
            visibleHeight,
            0,
            0,
            visibleWidth,
            visibleHeight
          );
        }

        const actualCroppedBounds = {
          left: integerLeft / canvasScale,
          top: integerTop / canvasScale,
          right: integerRight / canvasScale,
          bottom: integerBottom / canvasScale,
        };

        const blob = await new Promise(resolve => {
          outputCanvas.toBlob(result => resolve(result), 'image/png', 0.92);
        });
        if (!blob) {
          return null;
        }
        return {
          blob,
          croppedBounds: actualCroppedBounds,
          placementOrigin: {x: placementLeft, y: placementTop},
        };
      } finally {
        // Restore all canvas/renderer state so the preview isn't disrupted.
        if (originalActiveLayer) {
          canvas?.setActiveLayer?.(originalActiveLayer);
        }
        visibilitySnapshot.forEach(({obj, enabled}) => {
          obj.enabled = enabled;
        });
        if (stashedParams?.boundingBox) {
          renderer.boundingBox.position.x = stashedParams.boundingBox.x;
          renderer.boundingBox.position.y = stashedParams.boundingBox.y;
        }
        renderer.restoreExportParams();
        renderer.engineParams.exportMode = false;
        if (canvasScaleSnapshot !== undefined) {
          canvas.params.scale = canvasScaleSnapshot;
        }
        if (globalTranslationSnapshot) {
          canvas.params.globalTranslation = globalTranslationSnapshot;
        }
        canvas?.adjustGlobalTranslation?.(canvas.params.scale, true);
        canvas?.render?.();
        if (!canvas?.render) {
          renderer.render();
        }
        outputCanvas?.destroy?.();
      }
    },

    async _uploadPersonalizationImages(blobs) {
      if (!blobs?.length) {
        return [];
      }

      // PHP's max_file_uploads defaults to 20, so we chunk to stay within that.
      const chunkSize = 20;
      const allFileIds = [];

      for (let start = 0; start < blobs.length; start += chunkSize) {
        const chunk = blobs.slice(start, start + chunkSize);
        const formData = new FormData();
        chunk.forEach((blob, i) => {
          formData.append(
            'files[]',
            blob,
            `personalization-element-${start + i}.${
              blob.type?.split('/')[1] || 'png'
            }`
          );
        });

        const {data} = await http.post(
          formatUrl('/omnis/v3/files/bulk'),
          formData
        );
        const items = data?.data ?? data;
        if (!Array.isArray(items)) {
          throw new Error('Unexpected response from file upload');
        }
        allFileIds.push(...items.map(item => item?.id ?? null));
      }

      return allFileIds;
    },

    // Hides placeholder objects (no form input) and returns a function that recovers hidden objects.
    _hidePlaceholderTemplateObjects(activeIds) {
      const hidden = [];
      for (const formItem of this.personalizationFormData) {
        const shouldHide = activeIds
          ? !activeIds.has(formItem.id)
          : !this._isPersonalizationValueExportable(formItem);

        if (!shouldHide) continue;

        const obj = this.getCanvasForLayer(formItem.layer)?.findObject(
          formItem.id
        );
        if (obj?.enabled) {
          obj.enabled = false;
          hidden.push(obj);
        }
      }
      return () => hidden.forEach(obj => (obj.enabled = true));
    },

    exportCanvasLayers() {
      return new Promise(resolve => {
        this.exportCanvasLayer(this.getCanvasForLayer('front'), 'front').then(
          frontDesignFile => {
            this.exportCanvasLayer(this.getCanvasForLayer('back'), 'back').then(
              backDesignFile => {
                resolve({frontDesignFile, backDesignFile});
              }
            );
          }
        );
      });
    },

    exportCanvasLayer(
      canvas,
      layer,
      format = 'image/png',
      targetDesignWidth = 2584
    ) {
      return new Promise(resolve => {
        if (!canvas) {
          resolve(null);
          return;
        }

        const objects = canvas.getObjects(layer);
        const exportableObjects = objects.filter(
          obj => obj?.enabled && !obj?.applicationGroupRef
        );
        if (!exportableObjects.length) {
          resolve(null);
          return;
        }
        const designScale = targetDesignWidth / canvas.boundingBox.width;
        const visibilitySnapshot = objects.map(obj => ({
          obj,
          enabled: obj.enabled,
        }));

        visibilitySnapshot.forEach(({obj}) => {
          if (obj?.applicationGroupRef) {
            obj.enabled = false;
          }
        });

        const output = canvas.export(format, 0.92, designScale, 1, false);

        visibilitySnapshot.forEach(({obj, enabled}) => {
          obj.enabled = enabled;
        });

        resolve(output);
      });
    },
  },
};
</script>
